import React, { useState } from 'react'
import { Box, Chip, Collapse, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { ChevronRight, ExpandMore, MoreVert } from '@mui/icons-material'

const styles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minHeight: 40,
    borderRadius: 1,
    pr: 1,
    '&:hover': { backgroundColor: 'action.hover' },
  },
  toggle: { width: 28, height: 28, flexShrink: 0 },
  name: { fontWeight: 600, whiteSpace: 'nowrap' },
  muted: { color: 'text.secondary', whiteSpace: 'nowrap' },
  email: {
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 260,
  },
  counts: { color: 'text.secondary', whiteSpace: 'nowrap', ml: 'auto', pl: 2 },
  branch: { borderLeft: 1, borderColor: 'divider' },
}

// Colores de los chips por clave del tema; nunca un hex suelto (principio V y VIII)
const typeColor = {
  H2H: 'info',
  DISTRIBUIDOR: 'primary',
  MENUDEO: 'default',
}

// Lo que llega del backend no se da por bueno: todo campo ausente tiene su guion
const text = (value) => {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number' && !isNaN(value)) return String(value)
  return '-'
}

const count = (value) => (typeof value === 'number' && !isNaN(value) ? value : 0)

const CommerceTreeNode = ({
  node,
  level = 0,
  expanded = {},
  actions = [],
  onToggle,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null)

  const children = Array.isArray(node?.children) ? node.children : []
  const salePointCount = count(node?.salePoints)
  const isOpen = Boolean(expanded[node?._id])
  const canExpand = children.length > 0

  const available = actions.filter((action) => action && (!action.validate || action.validate(node)))

  const handleAction = (action) => {
    setMenuAnchor(null)
    action.onClick(node)
  }

  return (
    <Box>
      <Box sx={{ ...styles.row, pl: level * 3 }}>
        {canExpand ? (
          <IconButton
            sx={styles.toggle}
            size="small"
            onClick={() => onToggle(node)}
            aria-label={isOpen ? 'Contraer' : 'Expandir'}
          >
            {isOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={styles.toggle} />
        )}

        <Typography variant="body2" sx={styles.muted}>
          {text(node?.identification)}
        </Typography>

        <Typography variant="body2" sx={styles.name}>
          {typeof node?.username === 'string' && node.username.trim() ? node.username.trim() : 'Sin nombre'}
        </Typography>

        <Chip
          size="small"
          label={text(node?.type_commerce)}
          color={typeColor[node?.type_commerce] || 'default'}
          variant="outlined"
        />

        {/* Activo es lo normal y no se marca: sólo se señala lo que no lo está */}
        {node?.state === false && <Chip size="small" label="Inactivo" color="error" />}
        {typeof node?.state !== 'boolean' && <Chip size="small" label="-" variant="outlined" />}

        <Tooltip title={text(node?.user?.email)}>
          <Typography variant="body2" sx={styles.email}>
            {text(node?.user?.email)}
          </Typography>
        </Tooltip>

        <Typography variant="caption" sx={styles.counts}>
          {children.length} {children.length === 1 ? 'comercio' : 'comercios'}
          {' · '}
          {salePointCount} {salePointCount === 1 ? 'punto de venta activo' : 'puntos de venta activos'}
        </Typography>

        {available.length > 0 && (
          <IconButton size="small" onClick={(event) => setMenuAnchor(event.currentTarget)} aria-label="Acciones">
            <MoreVert fontSize="small" />
          </IconButton>
        )}

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {available.map((action) => (
            <MenuItem
              key={action.label}
              disabled={Boolean(action.disabled && action.disabled(node))}
              onClick={() => handleAction(action)}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box sx={{ ...styles.branch, ml: level * 3 + 2 }}>
          {children.map((child) => (
            <CommerceTreeNode
              key={child._id}
              node={child}
              level={level + 1}
              expanded={expanded}
              actions={actions}
              onToggle={onToggle}
            />
          ))}

        </Box>
      </Collapse>
    </Box>
  )
}

export default CommerceTreeNode
